<template>
  <div v-if="errorMessage" class="rounded-md w-full bg-red-100 px-4 py-3 text-sm text-red-700">
    {{ errorMessage }}
  </div>
  <div v-else-if="isLoading" class="flex flex-col flex-auto justify-center text-center">
    <LayupSpinner />
    <p class="text-black">Loading...</p>
  </div>
  <div v-else class="flex flex-col text-left divide-y h-full w-full">
    <div v-if="shouldShowPeach" class="grow py-1 mb-2">
      <div class="flex cursor-pointer mb-3" @click="back">
        <img src="@/assets/icons/backArrow.svg" />
        <div class="self-center text-center text-lg font-semibold grow text-black">Checkout</div>
      </div>
      <div id="payment-form" :class="{ 'h-[500px]': checkoutFormLoaded }"></div>
    </div>
    <div class="flex flex-row py-2">
      <div class="flex flex-1 w-auto">
        <p class="text-extrabold text-base-black text-sm mr-1">Amount Due</p>
        <div class="mt-0.5">
          <ToolTip
            :text="'The amount due contributes towards or secures your product for the duration of the lay-by agreement.'"
          />
        </div>
      </div>
      <div class="font-bold text-base-black">
        {{ amountDisplay }}
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import ToolTip from '@/components/common/ToolTip.vue';
import { BASE_URL } from '@/config';
import { NetworkError } from '@/errors';
import { createPayment } from '@/services/payments';
import { useWidgetStore } from '@/stores/widget';
import { displayCurrency } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

const widgetStore = useWidgetStore();

const router = useRouter();

const isLoading = ref(false);
const errorMessage = ref('');
const redirectParams = ref({ id: '' });
const checkoutFormLoaded = ref(false);
const paymentMethod = router.currentRoute.value.query.paymentMethod as string;
const amountDisplay = ref(displayCurrency(widgetStore.amountDue));

const shouldShowPeach = computed(() => {
  return (
    (paymentMethod === 'CARD' && widgetStore.order.paymentMethods.creditOrDebitCard) ||
    (paymentMethod === 'EFT' && widgetStore.order.paymentMethods.eft)
  );
});

const init = async () => {
  isLoading.value = true;
  if (!shouldShowPeach.value) {
    return router.replace({ name: 'SelectPaymentMethod' });
  }
  await makePayment();
};

const back = () => {
  router.replace({ name: 'SelectPaymentMethod' });
};

const loadCheckoutForm = async (checkoutId: string, entityId: string) => {
  isLoading.value = false;
  if ((window as any).Checkout) {
    const checkout = await (window as any).Checkout.initiate({
      checkoutId,
      key: entityId, // Replace with your public entity key
      events: {
        onCompleted: (value: any) => {
          if (
            !value.registrationId ||
            value.registrationId == null ||
            value.registrationId === '' ||
            value.registrationId === undefined ||
            value.registrationId === 'undefined'
          ) {
            router.replace({
              name: 'paymentStatus',
              params: redirectParams.value,
              query: { isEmbeddedCheckout: 'true' },
            });
          } else {
            router.replace({
              name: 'paymentStatus',
              params: redirectParams.value,
              query: { isEmbeddedCheckout: 'true', registrationId: value.registrationId },
            });
          }
        },
        onCancelled: () => {
          router.replace({
            name: 'paymentStatus',
            params: redirectParams.value,
            query: { isEmbeddedCheckout: 'true' },
          });
        },
        onExpired: () => {
          router.replace({
            name: 'paymentStatus',
            params: redirectParams.value,
            query: { isEmbeddedCheckout: 'true' },
          });
        },
      },
      options: {
        theme: {
          brand: {
            primary: '#1295a5',
          },
        },
      },
    });

    checkout.render('#payment-form');
    checkoutFormLoaded.value = true;
  } else {
    createException({ message: 'Checkout SDK not loaded.', checkoutId });
  }
};

const makePayment = async () => {
  try {
    const paymentPlanId = widgetStore.id;
    const notificationUrl = BASE_URL + '/shopper/payment';
    const response = await createPayment(
      paymentPlanId,
      widgetStore.amountDue / 100,
      notificationUrl,
      paymentMethod,
      true,
      'paymentPlan',
    );
    const paymentId = response._id;
    const { checkoutId, entityId } = response;
    redirectParams.value = { id: paymentId };
    await loadCheckoutForm(checkoutId, entityId!);
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('create_payment', { status: 'network_error' });
      errorMessage.value = 'Network Error. Please try again.';
    } else {
      createException(error);
      addMetric('create_payment', { status: 'error', channel: 'widget', method: 'card' });
      if (error?.response?.data) {
        errorMessage.value = error.response.data;
      } else {
        errorMessage.value = 'An unexpected error occurred. Payment could not be created. Please contact support.';
      }
    }
  }
};

init();
</script>
<style>
@media (max-width: 767px) {
  iframe[name='peach-method-CNP'] {
    zoom: 0.8;
  }
}
</style>
